/**
 * Excerpt engine — verify-at-source P1 (Ale 2026-08-03).
 *
 * Renders the document-pixel excerpt behind a claim: fetch the PDF once,
 * render the cited page off-screen, locate the extractor's verbatim snippet
 * in the text layer (space-insensitive — PDF text items carry no reliable
 * whitespace at fragment boundaries, the dead-PdfViewer lesson), and crop a
 * full-width strip around the match with a little context.
 *
 * Honesty states, never silent: `located` (strip crops the match),
 * `page_only` (passage not findable — full-page thumbnail instead),
 * `no_text` (scanned page, no text layer — thumbnail + manual-verify note).
 * Everything is cached per document / page / snippet so focus changes and
 * re-renders never refetch or re-rasterize.
 */
import { pdfjs } from 'react-pdf'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { apiFetchBlob } from './api'
import { getToken } from './auth'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

export type ExcerptStatus = 'located' | 'page_only' | 'no_text' | 'error'

export interface ExcerptResult {
  status: ExcerptStatus
  /** PNG data URL — the cropped strip (located) or a page thumbnail. */
  imageUrl: string | null
  page: number
}

/** Collapse whitespace, strip diacritics, lowercase. */
function normalizeText(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim().toLowerCase()
}

/** Space-insensitive variant — robust to text-layer fragmentation. */
function normalizeTight(text: string): string {
  return normalizeText(text).replace(/\s+/g, '')
}

const RENDER_SCALE = 1.6
const CONTEXT_PAD_PX = 34 // context above/below the match, at render scale
const MAX_DOCS = 4
const MAX_PAGES = 12

const docCache = new Map<string, Promise<PDFDocumentProxy>>()
let activeSession: string | null = null
let generation = 0
function clearPrivateExcerpts(): void {
  generation += 1
  for (const pending of docCache.values()) pending.then((doc) => doc.destroy()).catch(() => {})
  docCache.clear()
  pageCache.clear()
  excerptCache.clear()
  pageTightCache.clear()
  activeSession = getToken()
}
function checkExcerptSession(): void {
  if (activeSession !== getToken()) clearPrivateExcerpts()
}
if (typeof window !== 'undefined') {
  window.addEventListener('nolos-auth-changed', clearPrivateExcerpts)
  window.addEventListener('storage', (event) => {
    if (event.key === 'nolos_jwt') clearPrivateExcerpts()
  })
}

function getDoc(documentId: string): Promise<PDFDocumentProxy> {
  checkExcerptSession()
  const requestedGeneration = generation
  const cached = docCache.get(documentId)
  if (cached) return cached
  const p: Promise<PDFDocumentProxy> = (async () => {
    const blob = await apiFetchBlob(`/api/documents/${encodeURIComponent(documentId)}/content`)
    const data = await blob.arrayBuffer()
    const document = await pdfjs.getDocument({ data }).promise
    if (requestedGeneration !== generation) {
      await document.destroy()
      throw new Error('Document session changed')
    }
    return document
  })()
  docCache.set(documentId, p)
  if (docCache.size > MAX_DOCS) {
    const oldest = docCache.keys().next().value
    if (oldest !== undefined && oldest !== documentId) {
      docCache
        .get(oldest)
        ?.then((d) => d.destroy())
        .catch(() => {})
      docCache.delete(oldest)
    }
  }
  return p
}

interface TextPiece {
  str: string
  transform: number[]
  width: number
  height: number
}

interface RenderedPage {
  canvas: HTMLCanvasElement
  toViewport: (x: number, y: number) => [number, number]
  pieces: TextPiece[]
}

const pageCache = new Map<string, Promise<RenderedPage>>()

function renderPage(documentId: string, pageNum: number): Promise<RenderedPage> {
  const key = `${documentId}:${pageNum}`
  const cached = pageCache.get(key)
  if (cached) return cached
  const requestedGeneration = generation
  const p: Promise<RenderedPage> = (async () => {
    const doc = await getDoc(documentId)
    const page = await doc.getPage(Math.max(1, Math.min(pageNum, doc.numPages)))
    const viewport = page.getViewport({ scale: RENDER_SCALE })
    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(viewport.width)
    canvas.height = Math.ceil(viewport.height)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no 2d context')
    await page.render({ canvas, canvasContext: ctx, viewport }).promise
    const tc = await page.getTextContent()
    const pieces: TextPiece[] = []
    for (const it of tc.items) {
      const cand = it as { str?: unknown; transform?: unknown; width?: unknown; height?: unknown }
      if (typeof cand.str === 'string' && Array.isArray(cand.transform)) {
        pieces.push({
          str: cand.str,
          transform: cand.transform as number[],
          width: typeof cand.width === 'number' ? cand.width : 0,
          height: typeof cand.height === 'number' ? cand.height : 0,
        })
      }
    }
    if (requestedGeneration !== generation) throw new Error('Document session changed')
    return {
      canvas,
      toViewport: (x: number, y: number) => {
        const [vx, vy] = viewport.convertToViewportPoint(x, y)
        return [vx, vy]
      },
      pieces,
    }
  })()
  pageCache.set(key, p)
  if (pageCache.size > MAX_PAGES) {
    const oldest = pageCache.keys().next().value
    if (oldest !== undefined && oldest !== key) pageCache.delete(oldest)
  }
  return p
}

/** Vertical [top, bottom] of the snippet on the rendered page, or null. */
function locateSnippet(rp: RenderedPage, text: string): { top: number; bottom: number } | null {
  const target = normalizeTight(text)
  if (!target) return null
  // Tight-concatenate all pieces, remembering each piece's tight range.
  let tight = ''
  const ranges: { start: number; end: number; piece: TextPiece }[] = []
  for (const piece of rp.pieces) {
    const t = normalizeTight(piece.str)
    if (!t) continue
    ranges.push({ start: tight.length, end: tight.length + t.length, piece })
    tight += t
  }
  const idx = tight.indexOf(target)
  if (idx < 0) return null
  const end = idx + target.length
  let top = Infinity
  let bottom = -Infinity
  for (const r of ranges) {
    if (r.end <= idx || r.start >= end) continue
    const t = r.piece.transform
    const x = t[4]
    const y = t[5]
    // Baseline corner and the opposite corner (x+width, y+height) bound the
    // piece well enough for a horizontal strip crop.
    const [, vy1] = rp.toViewport(x, y)
    const [, vy2] = rp.toViewport(x + r.piece.width, y + r.piece.height)
    top = Math.min(top, vy1, vy2)
    bottom = Math.max(bottom, vy1, vy2)
  }
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) return null
  return { top, bottom }
}

/** Nudge a horizontal cut to the nearest text-free row so the strip never
 *  slices through a line of type (scan limit keeps it O(small)). */
function snapToWhitespace(canvas: HTMLCanvasElement, y: number, dir: -1 | 1): number {
  const ctx = canvas.getContext('2d')
  if (!ctx) return y
  const maxScan = 28
  for (let off = 0; off <= maxScan; off++) {
    const yy = y + dir * off
    if (yy <= 0 || yy >= canvas.height - 1) return Math.max(0, Math.min(canvas.height, yy))
    const row = ctx.getImageData(0, yy, canvas.width, 1).data
    let dark = 0
    for (let i = 0; i < row.length; i += 16) {
      if (row[i] < 200 || row[i + 1] < 200 || row[i + 2] < 200) dark++
    }
    if (dark === 0) return yy
  }
  return y
}

function cropStrip(canvas: HTMLCanvasElement, top: number, bottom: number): string {
  let y0 = Math.max(0, Math.floor(top - CONTEXT_PAD_PX))
  y0 = snapToWhitespace(canvas, y0, -1)
  // Cap the strip: a "snippet" that spans most of the page is a thumbnail,
  // not an excerpt — the caller's page_only state reads more honestly.
  let y1 = Math.min(canvas.height, Math.ceil(bottom + CONTEXT_PAD_PX), y0 + canvas.height * 0.55)
  y1 = snapToWhitespace(canvas, y1, 1)
  const out = document.createElement('canvas')
  out.width = canvas.width
  out.height = Math.max(1, y1 - y0)
  out
    .getContext('2d')
    ?.drawImage(canvas, 0, y0, canvas.width, out.height, 0, 0, canvas.width, out.height)
  return out.toDataURL('image/png')
}

/** Fallback when the passage can't be pinpointed: the TOP of the cited page
 *  at full resolution — readable document pixels (title, parties, headers
 *  live there), never a shrunken whole-page postage stamp. */
function topStrip(canvas: HTMLCanvasElement): string {
  return cropStrip(canvas, CONTEXT_PAD_PX, canvas.height * 0.34)
}

const excerptCache = new Map<string, Promise<ExcerptResult>>()

/**
 * The excerpt behind one claim: document + cited page + verbatim snippet.
 * Never rejects — failures come back as `{status: 'error'}`.
 */
export function renderExcerpt(
  documentId: string,
  page: number,
  text?: string | null
): Promise<ExcerptResult> {
  checkExcerptSession()
  const key = `${documentId}:${page}:${text ?? ''}`
  const cached = excerptCache.get(key)
  if (cached) return cached
  const p: Promise<ExcerptResult> = (async (): Promise<ExcerptResult> => {
    const rp = await renderPage(documentId, page)
    const hasText = rp.pieces.some((pc) => normalizeTight(pc.str).length > 0)
    if (!hasText) return { status: 'no_text', imageUrl: topStrip(rp.canvas), page }
    if (text) {
      const hit = locateSnippet(rp, text)
      if (hit)
        return { status: 'located', imageUrl: cropStrip(rp.canvas, hit.top, hit.bottom), page }
    }
    return { status: 'page_only', imageUrl: topStrip(rp.canvas), page }
  })().catch((): ExcerptResult => {
    excerptCache.delete(key) // don't cache transient fetch failures
    return { status: 'error', imageUrl: null, page }
  })
  excerptCache.set(key, p)
  return p
}

// ── Value search (P2a) ────────────────────────────────────────────────────
// Evidence rows carry values but no location (that's the P2b write-time
// fix). Interim: scan the document's text layers for the asserted value —
// text extraction only, no rasterizing — and render an excerpt of the first
// page that contains it.

const MAX_SCAN_PAGES = 20

const pageTightCache = new Map<string, Promise<string>>()

function pageTightText(documentId: string, pageNum: number): Promise<string> {
  const key = `${documentId}:${pageNum}`
  const cached = pageTightCache.get(key)
  if (cached) return cached
  const p: Promise<string> = (async () => {
    const doc = await getDoc(documentId)
    const page = await doc.getPage(pageNum)
    const tc = await page.getTextContent()
    let tight = ''
    for (const it of tc.items) {
      const s = (it as { str?: unknown }).str
      if (typeof s === 'string') tight += normalizeTight(s)
    }
    return tight
  })()
  pageTightCache.set(key, p)
  return p
}

/**
 * Find `value` anywhere in the document (first MAX_SCAN_PAGES pages) and
 * excerpt the page it appears on. Returns null when the value genuinely
 * isn't in the text — the caller states that honestly rather than showing
 * an unrelated page.
 */
export async function findValueExcerpt(
  documentId: string,
  value: string
): Promise<ExcerptResult | null> {
  const target = normalizeTight(value)
  if (!target) return null
  try {
    const doc = await getDoc(documentId)
    const pages = Math.min(doc.numPages, MAX_SCAN_PAGES)
    for (let pageNum = 1; pageNum <= pages; pageNum++) {
      const tight = await pageTightText(documentId, pageNum)
      if (tight.includes(target)) return renderExcerpt(documentId, pageNum, value)
    }
    return null
  } catch {
    return null
  }
}
